import type { Theme } from '@/hooks/useTheme'
import { cn } from '@/lib/utils'

/**
 * A miniature of the app drawn in one theme (design board "ThemePreview"):
 * sidebar with an active row, header, a KPI card with two chart series, a
 * table with two status pills, and a primary and an outline button.
 *
 * It sets `data-theme` on itself, so every token inside resolves to *that*
 * theme whatever the page is using — which is why `globals.css` declares its
 * aliases on `[data-theme]` as well as `:root`.
 *
 * Purely decorative: the picker's own label names the theme.
 */
export function ThemePreview({ theme }: { theme: Theme }) {
  return (
    <div
      data-theme={theme}
      aria-hidden="true"
      className="bg-background grid h-36 grid-cols-[4.5rem_1fr] overflow-hidden rounded-sm border"
    >
      <div className="bg-sidebar border-sidebar-border flex flex-col gap-1 border-r px-1.5 py-2">
        <div className="flex items-center gap-1.5 px-1.5 pb-1.5">
          <span className="bg-logo size-2.5 rounded-sm" />
          <span className="bg-sidebar-foreground h-1.5 w-7 rounded-full opacity-85" />
        </div>
        <div className="bg-sidebar-active relative flex h-4 items-center rounded-sm px-1.5">
          <span className="bg-sidebar-indicator absolute inset-y-1 -left-1.5 w-0.5" />
          <span className="bg-sidebar-active-foreground h-1 w-8 rounded-full" />
        </div>
        {['w-7', 'w-9', 'w-6'].map((width) => (
          <div key={width} className="flex h-4 items-center px-1.5">
            <span className={cn('bg-sidebar-label h-1 rounded-full opacity-70', width)} />
          </div>
        ))}
      </div>

      <div className="flex min-w-0 flex-col">
        <div className="bg-surface flex h-5 shrink-0 items-center gap-1.5 border-b px-2">
          <span className="border-border-control h-2.5 w-12 rounded-sm border" />
          <span className="bg-secondary ml-auto size-2.5 rounded-full" />
        </div>
        <div className="flex min-w-0 flex-col gap-1.5 p-2">
          <div className="bg-surface flex items-center gap-2 rounded-sm border px-2 py-1.5 shadow-xs">
            <div className="flex flex-col gap-1">
              <span className="bg-muted-foreground h-1 w-8 rounded-full" />
              <span className="bg-foreground h-2 w-10 rounded-full" />
            </div>
            <svg viewBox="0 0 80 24" preserveAspectRatio="none" className="h-5 min-w-0 flex-1">
              <polyline
                fill="none"
                strokeWidth="1.5"
                vectorEffect="non-scaling-stroke"
                className="stroke-chart-1"
                points="0,20 10,18 20,19 30,14 40,15 50,10 60,11 70,6 80,3"
              />
              <polyline
                fill="none"
                strokeWidth="1.5"
                vectorEffect="non-scaling-stroke"
                className="stroke-chart-2"
                points="0,22 10,21 20,20 30,20 40,18 50,18 60,16 70,15 80,13"
              />
            </svg>
          </div>
          <div className="bg-surface overflow-hidden rounded-sm border">
            <div className="bg-surface-muted h-2.5 border-b" />
            <div className="border-border-subtle flex h-4 items-center justify-between border-b px-1.5">
              <span className="bg-foreground-secondary h-1 w-10 rounded-full opacity-60" />
              <span className="bg-success-subtle h-2.5 w-7 rounded-sm" />
            </div>
            <div className="flex h-4 items-center justify-between px-1.5">
              <span className="bg-foreground-secondary h-1 w-8 rounded-full opacity-60" />
              <span className="bg-warning-subtle h-2.5 w-7 rounded-sm" />
            </div>
          </div>
          <div className="flex gap-1">
            <span className="bg-primary h-3.5 w-12 rounded-sm" />
            <span className="border-border-strong bg-surface h-3.5 w-9 rounded-sm border" />
          </div>
        </div>
      </div>
    </div>
  )
}
